import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { _getEntityRole, _getEntityRoles, _getPermissionCatalog } from '@/api/entity-roles/queries';
import { Permission, PERMISSION_METADATA } from '@/api/permissions';
import { useCreateRoleRequest } from '@/api/role-requests/mutations';
import { _getRoleRequests } from '@/api/role-requests/queries';
import { RoleRequestStatus, RoleRequestType } from '@/api/role-requests/types';
import { Button, EmptyState, Loader, Modal, Tabs, theme, Typography } from '@/shared/design-system';
import { RequirePermissionInline, useCan } from '@/shared/modules/permissions';
import {
  BOARD_VIEWS,
  BoardHeader,
  BoardPanes,
  type BoardView,
  ReadOnlyNotice,
  toBoardView,
} from '@/shared/modules/role-board/components/board-chrome';
import { UnsavedDraftsGuard } from '@/shared/modules/role-board/components/permission-draft/unsaved-drafts-guard';
import { RoleActivity } from '@/shared/modules/role-board/components/role-activity';
import { RoleCompare } from '@/shared/modules/role-board/components/role-compare';
import { RoleCreateModal } from '@/shared/modules/role-board/components/role-create-modal';
import { RoleDuplicateModal } from '@/shared/modules/role-board/components/role-duplicate-modal';
import { RolePanelHeader } from '@/shared/modules/role-board/components/role-panel-header';
import { roleSubtitle } from '@/shared/modules/role-board/role-descriptions';
import {
  PermissionDraftProvider,
  usePermissionDraftStore,
} from '@/shared/modules/role-board/use-permission-draft';
import { useRolePermissionSets } from '@/shared/modules/role-board/use-role-permission-sets';
import { useSelectedUserConfigSlice } from '@/stores/selectors';
import { canGrantProtectedPermissions } from '@/utils/permission-catalog';
import { pendingRequestCountsByRole, roleActivity } from '@/utils/role-request-activity';

import { isOwnerRole, ROLE_TABS, RoleDialogState, RoleTabKey } from './common';
import { OWNER_ROLE_TOOLTIP, RoleList, SYSTEM_ROLE_TOOLTIP } from './components/role-list';
import { RoleMembersTab } from './modules/role-members-tab';
import { RoleNameModal } from './modules/role-name-modal';
import { RolePermissionsTab } from './modules/role-permissions-tab';
import { CenteredState, MainPanel, PageRoot, TabPanelContent } from './styled';
import { useCallerHeldRoles } from './use-caller-held-roles';
import { useCallerIsOwner } from './use-caller-is-owner';
import { useRoleAdminAccess } from './use-role-admin-access';

export const PAGE_TITLE = 'Roles & permissions';
export const PAGE_DESCRIPTION =
  'A role is a named set of permissions. People can hold several roles and get everything those roles allow. Roles only add access, never take it away.';
export const READ_ONLY_DESCRIPTION = `Creating, editing and assigning roles needs the ${PERMISSION_METADATA[Permission.EntityManageRoles].label} or ${PERMISSION_METADATA[Permission.EntityAssignRoles].label} permission. An Owner or Admin of this organisation can grant it.`;
export const NO_ROLES_MESSAGE = 'This organisation has no roles yet';
export const ROLES_LOAD_ERROR = "Couldn't load roles";
export const NO_ENTITY_TITLE = 'Switch to an entity account to manage roles';
export const NO_ENTITY_MESSAGE =
  "Roles belong to an organisation. Gamma-plane roles live on their own page in the admin session's sidebar.";
export const ACCESS_DENIED_MESSAGE = "You don't have permission to view this entity's roles.";

const EntityRolesBoardContent = () => {
  const { selectedEntityId: entityId } = useSelectedUserConfigSlice();
  const { canManage, canAssign, heldPermissions } = useRoleAdminAccess();
  const canViewRoles = useCan(Permission.EntityViewRoles);
  const canApprove = useCan(Permission.EntityApproveRoleRequests);
  const canViewRequests = canViewRoles || canApprove;
  const heldRoleIds = useCallerHeldRoles(entityId);
  const isOwner = useCallerIsOwner(entityId);

  const [searchParams, setSearchParams] = useSearchParams();
  const roleIdParam = searchParams.get('roleId') ?? undefined;
  // A role is first read for what it grants, so Permissions opens by default; Members and
  // Activity are one click away and keep their place in the URL.
  const tabParam = (searchParams.get('roleTab') as RoleTabKey) ?? ROLE_TABS.permissions;
  const view: BoardView = toBoardView(searchParams.get('view'));

  const [dialog, setDialog] = useState<RoleDialogState>(null);
  const draftStore = usePermissionDraftStore();

  const roles = useQuery(_getEntityRoles(entityId));
  const roleList = useMemo(() => roles.data?.roles ?? [], [roles.data]);

  const selectedRoleId = useMemo(() => {
    if (roleIdParam && roleList.some((role) => role.id === roleIdParam)) return roleIdParam;

    return roleList[0]?.id;
  }, [roleList, roleIdParam]);

  const selectedRole = roleList.find((role) => role.id === selectedRoleId);

  // Keep the URL honest once the default selection resolves, so the view is linkable. Skipped
  // while the list is refetching: right after create/duplicate, `roleId` points at a row the
  // in-flight refetch hasn't returned yet, and correcting here would overwrite that id with the
  // fallback before the fresh list (which does contain it) ever lands — permanently, since once
  // corrected the two params match and this effect stops re-checking.
  useEffect(() => {
    if (!selectedRoleId || roleIdParam === selectedRoleId || roles.isFetching) return;

    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set('roleId', selectedRoleId);

        return next;
      },
      { replace: true },
    );
  }, [selectedRoleId, roleIdParam, roles.isFetching, setSearchParams]);

  // Sets one or more params in a single navigation. Two sequential setSearchParams calls in the
  // same tick would each rebuild the query string from the same pre-update `current` closure, so
  // the second call would silently drop the first's key (react-router-dom evaluates the
  // functional form against the current render, not a queue) — openNewRole below relies on this
  // being one call.
  const setParams = (updates: Record<string, string>) =>
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      Object.entries(updates).forEach(([key, value]) => next.set(key, value));

      return next;
    });

  const setParam = (key: string, value: string) => setParams({ [key]: value });

  const sourceRole = useQuery(_getEntityRole(entityId, selectedRoleId));
  const catalog = useQuery(_getPermissionCatalog());
  const { mutate: createRoleRequest, isPending: isRequestPending } = useCreateRoleRequest({
    entityId,
  });
  const permissionSets = useRolePermissionSets(roleList, (roleId) =>
    _getEntityRole(entityId, roleId),
  );
  const pendingRequests = useQuery(
    _getRoleRequests(canViewRequests ? entityId : null, RoleRequestStatus.Pending),
  );
  const allRequests = useQuery(
    _getRoleRequests(canViewRequests && tabParam === ROLE_TABS.activity ? entityId : null),
  );

  const pendingCounts = useMemo(
    () => pendingRequestCountsByRole(pendingRequests.data?.requests ?? []),
    [pendingRequests.data],
  );
  const paletteSize = useMemo(
    () => catalog.data?.groups.reduce((total, group) => total + group.permissions.length, 0),
    [catalog.data],
  );

  const closeDialog = () => setDialog(null);

  // `pane` only matters on a phone, where the board shows the list or one role, not both.
  const selectRole = (roleId: string) =>
    setParams({ roleId, view: BOARD_VIEWS.list, pane: 'role' });
  const closeRolePane = () =>
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.delete('pane');

      return next;
    });

  const lockTooltip = (role: { systemKey: string | null }) =>
    isOwnerRole(role) ? OWNER_ROLE_TOOLTIP : SYSTEM_ROLE_TOOLTIP;

  // Roles are organisation-scoped, so there is nothing to show in a personal or
  // Gamma session. Checked before the query state because a query with no entity
  // is disabled, not failing, and must not be reported as an error.
  if (!entityId) {
    return (
      <CenteredState>
        <Typography variant="body2" color={theme.colors.text.primary}>
          {NO_ENTITY_TITLE}
        </Typography>
        <Typography variant="body3" color={theme.colors.text.secondary}>
          {NO_ENTITY_MESSAGE}
        </Typography>
      </CenteredState>
    );
  }

  if (roles.isPending) {
    return (
      <CenteredState>
        <Loader />
      </CenteredState>
    );
  }

  if (roles.isError) {
    return (
      <CenteredState>
        <Typography variant="body3" color={theme.colors.text.secondary}>
          {ROLES_LOAD_ERROR}
        </Typography>
      </CenteredState>
    );
  }

  return (
    <PermissionDraftProvider store={draftStore}>
      <PageRoot>
        <UnsavedDraftsGuard draftCount={draftStore.drafts.size} />
        <BoardHeader
          title={PAGE_TITLE}
          description={PAGE_DESCRIPTION}
          view={view}
          onViewChange={(next) => setParam('view', next)}
        />

        {!canManage && !canAssign && <ReadOnlyNotice description={READ_ONLY_DESCRIPTION} />}

        {view === BOARD_VIEWS.compare ? (
          <RoleCompare
            groups={catalog.data?.groups ?? []}
            columns={roleList.map((role) => ({
              role,
              permissions: permissionSets.sets.get(role.id),
            }))}
            catalogLoading={catalog.isPending}
            loadFailed={permissionSets.isError || catalog.isError}
            onRetry={() => {
              permissionSets.retry();
              if (catalog.isError) {
                void catalog.refetch();
              }
            }}
            onOpenRole={selectRole}
          />
        ) : (
          <BoardPanes
            isDetailOpen={searchParams.get('pane') === 'role'}
            onBack={closeRolePane}
            list={
              <RoleList
                roles={roleList}
                selectedRoleId={selectedRoleId}
                onSelect={selectRole}
                canManage={canManage}
                onCreate={() => setDialog({ kind: 'new' })}
                isHeldByCaller={(role) => heldRoleIds.has(role.id)}
                subtitle={(role) => roleSubtitle(role, permissionSets.sets.get(role.id)?.size)}
                pendingCount={(role) => pendingCounts.get(role.id) ?? 0}
              />
            }
            detail={
              <MainPanel>
                {!selectedRole ? (
                  <EmptyState title={NO_ROLES_MESSAGE} />
                ) : (
                  <>
                    <RolePanelHeader
                      role={selectedRole}
                      permissionCount={permissionSets.sets.get(selectedRole.id)?.size}
                      paletteSize={paletteSize}
                      lockTooltip={lockTooltip(selectedRole)}
                      actions={
                        canManage && (
                          <>
                            {!isOwnerRole(selectedRole) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setDialog({ kind: 'duplicate' })}
                              >
                                Duplicate
                              </Button>
                            )}
                            {!selectedRole.isLocked && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setDialog({ kind: 'rename' })}
                                >
                                  Rename
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  color="destructive"
                                  onClick={() => setDialog({ kind: 'delete' })}
                                >
                                  Delete
                                </Button>
                              </>
                            )}
                          </>
                        )
                      }
                    />

                    <Tabs.Root
                      variant="bordered"
                      value={tabParam}
                      onValueChange={(value) => setParam('roleTab', String(value))}
                    >
                      <Tabs.List>
                        <Tabs.Tab value={ROLE_TABS.permissions} label="Permissions" />
                        <Tabs.Tab
                          value={ROLE_TABS.members}
                          label="Members"
                          counter={selectedRole.memberCount}
                        />
                        {canViewRequests && (
                          <Tabs.Tab value={ROLE_TABS.activity} label="Activity" />
                        )}
                      </Tabs.List>

                      <Tabs.Panel value={ROLE_TABS.permissions}>
                        <TabPanelContent>
                          <RolePermissionsTab
                            entityId={entityId}
                            roleId={selectedRole.id}
                            pendingRequests={pendingRequests.data?.requests ?? []}
                            canApprove={canApprove}
                          />
                        </TabPanelContent>
                      </Tabs.Panel>

                      <Tabs.Panel value={ROLE_TABS.members}>
                        <TabPanelContent>
                          <RoleMembersTab
                            entityId={entityId}
                            roleId={selectedRole.id}
                            roleName={selectedRole.name}
                            isOwnerRole={isOwnerRole(selectedRole)}
                            pendingRequests={pendingRequests.data?.requests ?? []}
                          />
                        </TabPanelContent>
                      </Tabs.Panel>

                      {canViewRequests && (
                        <Tabs.Panel value={ROLE_TABS.activity}>
                          <TabPanelContent>
                            <RoleActivity
                              requests={roleActivity(
                                allRequests.data?.requests ?? [],
                                selectedRole,
                              )}
                              isLoading={allRequests.isPending}
                              isError={allRequests.isError}
                            />
                          </TabPanelContent>
                        </Tabs.Panel>
                      )}
                    </Tabs.Root>
                  </>
                )}
              </MainPanel>
            }
          />
        )}

        {selectedRole && (
          <RoleDuplicateModal
            open={dialog?.kind === 'duplicate'}
            sourceRoleName={selectedRole.name}
            sourcePermissions={sourceRole.data?.permissions}
            catalogGroups={catalog.data?.groups}
            canGrantProtected={canGrantProtectedPermissions({ isOwner, heldPermissions })}
            isPending={isRequestPending}
            onClose={closeDialog}
            onConfirm={(name, permissions) => {
              createRoleRequest(
                {
                  type: RoleRequestType.CreateRole,
                  payload: { sourceRoleId: selectedRole.id, name, permissions },
                },
                { onSuccess: closeDialog },
              );
            }}
          />
        )}

        <RoleCreateModal
          open={dialog?.kind === 'new'}
          catalogGroups={catalog.data?.groups}
          canGrantProtected={canGrantProtectedPermissions({ isOwner, heldPermissions })}
          isPending={isRequestPending}
          onClose={closeDialog}
          onConfirm={(name, permissions) => {
            createRoleRequest(
              { type: RoleRequestType.CreateRole, payload: { name, permissions } },
              { onSuccess: closeDialog },
            );
          }}
        />

        <RoleNameModal
          open={dialog?.kind === 'rename'}
          title={selectedRole ? `Rename ${selectedRole.name}` : 'Rename role'}
          description="Renaming a custom role is recorded as a role-change request."
          confirmLabel="Rename role"
          initialName={selectedRole?.name}
          isPending={isRequestPending}
          onClose={closeDialog}
          onConfirm={(name) => {
            if (!selectedRole || selectedRole.isLocked) return;

            createRoleRequest(
              { type: RoleRequestType.RenameRole, payload: { roleId: selectedRole.id, name } },
              { onSuccess: closeDialog },
            );
          }}
        />

        <Modal
          open={dialog?.kind === 'delete'}
          onOpenChange={(isOpen) => !isOpen && closeDialog()}
          variant="center"
          title={selectedRole ? `Delete ${selectedRole.name}?` : 'Delete role'}
          description={
            selectedRole?.memberCount
              ? `${selectedRole.memberCount} ${selectedRole.memberCount === 1 ? 'person still holds' : 'people still hold'} this role. Remove them on the Members tab first, and give them another role if they should keep this access.`
              : 'This cannot be undone. Nobody holds this role, so no one loses access.'
          }
          primaryAction={
            selectedRole?.memberCount
              ? {
                  label: 'Go to members',
                  onClick: () => {
                    setParam('roleTab', ROLE_TABS.members);
                    closeDialog();
                  },
                }
              : {
                  label: isRequestPending ? 'Submitting…' : 'Delete role',
                  color: 'destructive',
                  onClick: () => {
                    if (!selectedRole) return;

                    createRoleRequest(
                      { type: RoleRequestType.DeleteRole, payload: { roleId: selectedRole.id } },
                      { onSuccess: closeDialog },
                    );
                  },
                }
          }
          secondaryAction={{ label: 'Cancel', onClick: closeDialog }}
        >
          <></>
        </Modal>
      </PageRoot>
    </PermissionDraftProvider>
  );
};

/**
 * `RequirePermissionInline`, not `RequirePermission`: this gates one tab inside
 * a page that is reachable on `EntityView` alone. A hard redirect would
 * evict a team-viewer from Manage Team entirely over a narrower permission they
 * simply don't also hold.
 */
const EntityRolesBoard = () => (
  <RequirePermissionInline
    permission={Permission.EntityViewRoles}
    deniedMessage={ACCESS_DENIED_MESSAGE}
  >
    <EntityRolesBoardContent />
  </RequirePermissionInline>
);

export default EntityRolesBoard;
